/**
 * realisticCabinetMarkers — pinta cada cabinet como un "rayo" vertical
 * sobre la vista 3D realista.
 *
 * Decisión visual:
 *  - `Polyline3DInteractiveElement` vertical (suelo → ~30 m), color
 *    amarillo brillante. Un eje vertical por cabinet, fácil de localizar
 *    desde la cámara default y desde cualquier altura/ángulo razonable.
 *  - Anillo plano en la base: un `Polygon3DElement` circular fino que
 *    marca con halo amarillo el suelo bajo cada cabinet — sirve de
 *    referencia espacial cuando la cámara queda muy baja y los rayos
 *    apuntan fuera del viewport.
 *  - Click sobre el rayo → `useSelection.toggle(logicalId)`, mismo flujo
 *    que la vista MapLibre.
 *  - Color reactivo a `useMockMode` (verde "Simular red") y al store
 *    global; reasignar `strokeColor` es barato (no rebuilda la geometría).
 *
 * Sin animación de geometría:
 *  - Una versión previa animaba la altura del rayo con `requestAnimationFrame`
 *    usando `sin(t)`. Cada reasignación de `coordinates` provoca al SDK
 *    un rebuild completo del path 3D, lo que producía un flickering muy
 *    visible. Si queremos "vida" en el futuro, animar `strokeColor` u
 *    `outerColor` a baja frecuencia (≤2 fps) tiene menos coste que tocar
 *    la geometría.
 */
import type { IntersectionDefinition } from '../../types/intersection';
import { ColorCode as CC } from '../../types/intersection';
import { useSelection } from '../../store/selection';
import { useMockMode } from '../../store/mockMode';
import { usePinControls, hexToRgb, autoScaleForRange } from '../../store/realisticPinControls';
import { useColomaMqtt } from '../../store/coloma-mqtt';
import { useAlarmMasks, isAlarmMasked } from '../../store/alarmMasks';
import { useSessionAlarmMutes, isSessionMuted } from '../../store/sessionAlarmMutes';
import { useDetailedSimulation } from '../../store/detailedSimulation';
import { toCanonicalId, toDisplayId } from '../../utils/canonicalId';
import { rangeToZoom } from './RealisticMapView';
import { osmGroupMappingResolved as osmGroupMapping } from '../../data/osm-group-mapping-resolved';
import { santaColomaStaticSignals } from '../../data/santa-coloma';

// Override de paleta cuando "Simular red" está activo — sustituye el
// `modelColor` del store por un verde intenso ("toda la flota online").
const COLOR_MOCK_ONLINE = '#22c55e'; // green-500

// Colores fijos del fondo/borde del pin. El glyph y el modelo 3D son
// ajustables desde el panel `usePinControls`.
const PIN_BG_RGB = '203, 213, 225';      // slate-300
const PIN_BORDER_RGB = '71, 85, 105';    // slate-600

/**
 * Niveles discretos de tamaño según rango de cámara (metros). Discretos
 * a propósito: cambiar `coordinates` en cada gesto fluido provoca
 * flickering; cambiar al cruzar un umbral lo limita a 1 reasignación
 * puntual cuando el usuario cambia de "vista" — y en cambio sí cambiamos
 * `strokeWidth` de forma continua (cheap, sin flicker).
 */
interface Tier {
  /** rango mínimo de cámara (m) al que aplica este tier. */
  minRange: number;
  rayHeight: number;
  strokeWidth: number;
  haloRadiusDeg: number;
  labelOffset: number;
}
const TIERS: Tier[] = [
  // ordenados de más cerca a más lejos. labelOffset = 0 ancla el pin en la
  // punta del rayo, "rematándolo" en lugar de flotar separado.
  { minRange: 0,    rayHeight: 18, strokeWidth: 5,  haloRadiusDeg: 0.00004, labelOffset: 0 },
  { minRange: 600,  rayHeight: 30, strokeWidth: 8,  haloRadiusDeg: 0.00006, labelOffset: 0 },
  { minRange: 2000, rayHeight: 55, strokeWidth: 10, haloRadiusDeg: 0.00012, labelOffset: 0 },
  { minRange: 6000, rayHeight: 90, strokeWidth: 12, haloRadiusDeg: 0.00020, labelOffset: 0 },
];

function tierFor(range: number): Tier {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (range >= TIERS[i].minRange) return TIERS[i];
  }
  return TIERS[0];
}

interface MountOptions {
  intersections: IntersectionDefinition[];
}

const HALO_VERTICES = 12;

export function mountRealisticCabinetMarkers(
  mapEl: HTMLElement,
  opts: MountOptions,
): () => void {
  const maps3d = (window as any).google?.maps?.maps3d;
  if (!maps3d?.Polyline3DInteractiveElement) {
    return () => undefined;
  }

  const Polyline3DInteractiveElement = maps3d.Polyline3DInteractiveElement;
  const Polygon3DElement = maps3d.Polygon3DElement;
  const Marker3DElement = maps3d.Marker3DElement;
  const Model3DInteractiveElement = maps3d.Model3DInteractiveElement;
  const PinElement = (window as any).google?.maps?.marker?.PinElement;
  const AltitudeMode = maps3d.AltitudeMode;

  // Modelos del armario regulador (caja verde DGT, base de hormigón con
  // textura intemperizada, antena puck en el techo, halo doble emisivo
  // a ras del suelo que comunica estado desde lejos). 3 variantes según
  // estado MQTT — el halo es el que cambia de color entre ellas.
  const CABINET_URL_ON    = '/models/cabinet-on.glb';
  const CABINET_URL_OFF   = '/models/cabinet-off.glb';
  const CABINET_URL_ALARM = '/models/cabinet-alarm.glb';
  const CABINET_HEIGHT_M = 1.62;  // altura física real del cabinet
  const useSentinel = Boolean(Model3DInteractiveElement);

  /** Elige el .glb según estado MQTT actual del regulador. */
  function cabinetSrcFor(id: number): string {
    const canonical = toCanonicalId(id);
    const status = useColomaMqtt.getState().reguladores[canonical];
    const mock = useMockMode.getState().enabled;
    if (!status) {
      // Sin datos MQTT: en modo "Simular red" mostrar como online;
      // si no, como offline.
      return mock ? CABINET_URL_ON : CABINET_URL_OFF;
    }
    if (!status.online) return CABINET_URL_OFF;
    // Mismo criterio que el panel/contador: una alarma enmascarada (ARS-09)
    // o silenciada en la sesión no pinta el armario en alarma.
    const masks = useAlarmMasks.getState().masks;
    const muted = useSessionAlarmMutes.getState().muted;
    const activas = (status.alarmas || []).filter(
      code => !isAlarmMasked(masks, canonical, code) && !isSessionMuted(muted, canonical, code),
    );
    if (activas.length > 0) return CABINET_URL_ALARM;
    return CABINET_URL_ON;
  }

  /** Tier inicial — la cámara default ronda 800-1000 m. */
  let currentTier = tierFor(800);

  interface Cabinet {
    id: number;
    lat: number;
    lng: number;
    ray: HTMLElement;
    halo: HTMLElement | null;
    label: HTMLElement | null;
    display: string;
  }
  const cabinets: Cabinet[] = [];
  const listeners: Array<{ el: HTMLElement; type: string; fn: (e: Event) => void }> = [];

  function ringCoords(lat: number, lng: number, radiusDeg: number, alt = 0.5) {
    const out: Array<{ lat: number; lng: number; altitude: number }> = [];
    const cosLat = Math.cos((lat * Math.PI) / 180);
    for (let i = 0; i <= HALO_VERTICES; i++) {
      const t = (i / HALO_VERTICES) * Math.PI * 2;
      out.push({
        lat: lat + Math.cos(t) * radiusDeg,
        lng: lng + (Math.sin(t) * radiusDeg) / cosLat,
        altitude: alt,
      });
    }
    return out;
  }

  const altMode = AltitudeMode?.RELATIVE_TO_GROUND ?? 'RELATIVE_TO_GROUND';

  for (const ix of opts.intersections) {
    const [lng, lat] = ix.cabinet.position;
    const id = ix.cabinet.logicalId;
    const display = toDisplayId(id);

    // El "modelo 3D del regulador" puede ser:
    //  - Model3DInteractiveElement con `cabinet-{on|off|alarm}.glb`:
    //    armario DGT con halo emisivo que comunica estado. El src se
    //    reasigna en `updateCabinetStates()` cuando el estado MQTT
    //    cambia.
    //  - Polyline3DInteractiveElement vertical (rayo): fallback ligero
    //    si el SDK no expone Model3DInteractiveElement.
    let ray: HTMLElement;
    if (useSentinel && Model3DInteractiveElement) {
      const s = effectiveScale();
      ray = new Model3DInteractiveElement({
        position: { lat, lng, altitude: 0 },
        altitudeMode: altMode,
        src: cabinetSrcFor(id),
        // El .glb viene en metros (~1.62 m). modelScale del store es el
        // factor de exageración para que se distingan desde la cámara
        // default — calibrable con el slider del panel.
        scale: { x: s, y: s, z: s },
      }) as HTMLElement;
    } else {
      ray = new Polyline3DInteractiveElement({
        altitudeMode: altMode,
        coordinates: [
          { lat, lng, altitude: 0 },
          { lat, lng, altitude: currentTier.rayHeight },
        ],
        strokeColor: usePinControls.getState().modelColor,
        strokeWidth: currentTier.strokeWidth,
        drawsOccludedSegments: true,
      }) as HTMLElement;
    }

    const onClick = () => {
      useSelection.getState().toggle(id);
    };
    ray.addEventListener('gmp-click', onClick);
    listeners.push({ el: ray, type: 'gmp-click', fn: onClick });
    mapEl.appendChild(ray as Node);

    // El halo "anillo amarillo del SDK" se eliminó al migrar al modelo
    // cabinet-{on|off|alarm}.glb — cada .glb ya lleva su propio halo
    // doble emisivo integrado, codificado por color según estado. El
    // campo `halo` del cabinet queda como null por compatibilidad con
    // las funciones que lo manejan.
    const halo: HTMLElement | null = null;

    // Etiqueta flotando sobre el rayo: pin customizado con el número
    // dentro como glyph. `Marker3DElement` + `PinElement` (de la librería
    // `google.maps.marker`) sustituye el pin rojo default por uno con
    // fondo oscuro, contorno amarillo y el ID a 3 dígitos centrado.
    //
    // Los valores de opacidad / escala vienen del store
    // `usePinControls` — sliders en `RealisticPinControlsPanel` permiten
    // ajustarlos en vivo. La función `rebuildPin()` reemplaza el child
    // del marker cuando el store cambia, sin tocar el rayo ni el halo.
    // El pin del número se ancla justo por encima del cabinet. Con el
    // cabinet uso su altura escalada (1.62 m × modelScale + 1 m de
    // margen). Con el rayo, en la punta.
    const labelAlt = useSentinel
      ? CABINET_HEIGHT_M * effectiveScale() + 1
      : currentTier.rayHeight + currentTier.labelOffset;
    let label: HTMLElement | null = null;
    if (Marker3DElement && PinElement) {
      label = new Marker3DElement({
        position: { lat, lng, altitude: labelAlt },
        altitudeMode: altMode,
        drawsWhenOccluded: true,
      }) as HTMLElement;
      mapEl.appendChild(label as Node);
    }

    cabinets.push({ id, lat, lng, ray: ray as HTMLElement, halo, label, display });
  }

  // Color y escala "vista calle": cuando el operador baja la cámara a
  // nivel peatón, el número en cyan grande es más legible contra el
  // entorno fotorrealista. Por encima del umbral usamos los valores del
  // store (default verde fluor #42ff45 para vista municipio).
  //
  // El umbral se expresa en zoom MapLibre — la unidad familiar para los
  // operadores. La calibración range↔zoom (ZOOM_RANGE_CONST = 50M) la
  // mantiene `rangeToZoom()`: a lat ~41.45°, zoom 17.5 ≈ range 215 m,
  // zoom 18 ≈ 150 m, zoom 19 ≈ 75 m.
  const CLOSE_ZOOM = 17.5;
  const CLOSE_GLYPH_COLOR = '#00ffd5';
  const CLOSE_PIN_SCALE = 2.5;

  function isCloseView(): boolean {
    const range = (mapEl as any).range ?? 0;
    if (range <= 0) return false;
    const center = (mapEl as any).center;
    const lat = typeof center?.lat === 'function' ? center.lat() : (center?.lat ?? 41.45);
    return rangeToZoom(range, lat) >= CLOSE_ZOOM;
  }

  function effectivePinStyle() {
    const { pinOpacity, glyphOpacity, pinScale, glyphColor } = usePinControls.getState();
    if (isCloseView()) {
      return { pinOpacity, glyphOpacity, pinScale: CLOSE_PIN_SCALE, glyphColor: CLOSE_GLYPH_COLOR };
    }
    return { pinOpacity, glyphOpacity, pinScale, glyphColor };
  }

  // Helper de regeneración del PinElement con los valores actuales del
  // store (filtrados por `effectivePinStyle()` para el override de vista
  // calle). Lo llamamos al montar y en cada cambio de sliders. También
  // dispara `applyModelColor` por si cambió el color del modelo y
  // `applyModelScale` por si cambió la escala del Sentinel.
  function rebuildPins() {
    if (!PinElement) return;
    const { pinOpacity, glyphOpacity, pinScale, glyphColor } = effectivePinStyle();
    const glyphRgb = hexToRgb(glyphColor);
    for (const c of cabinets) {
      if (!c.label) continue;
      const pin = new PinElement({
        background: `rgba(${PIN_BG_RGB}, ${pinOpacity})`,
        borderColor: `rgba(${PIN_BORDER_RGB}, ${pinOpacity})`,
        glyphColor: `rgba(${glyphRgb}, ${glyphOpacity})`,
        glyph: c.display,
        scale: pinScale,
      });
      c.label.replaceChildren(pin.element);
    }
  }
  /**
   * Devuelve el factor de escala efectivo:
   *  - Si `autoScale` está ON → curva logarítmica según range de cámara.
   *    El cabinet se ve a tamaño real (1×) al acercarse y exagerado al
   *    alejarse para no perderlo entre los edificios fotogramétricos.
   *  - Si está OFF → valor del slider manual `modelScale` (1-30).
   */
  function effectiveScale(): number {
    const { autoScale, modelScale } = usePinControls.getState();
    if (!autoScale) return modelScale;
    const range = (mapEl as any).range ?? 800;
    return autoScaleForRange(range);
  }

  /** Ajusta `scale` del cabinet (y reubica el pin del número arriba). */
  function applyModelScale() {
    if (!useSentinel) return;
    const s = effectiveScale();
    const newLabelAlt = CABINET_HEIGHT_M * s + 1;
    for (const c of cabinets) {
      (c.ray as any).scale = { x: s, y: s, z: s };
      if (c.label) {
        (c.label as any).position = { lat: c.lat, lng: c.lng, altitude: newLabelAlt };
      }
    }
  }

  /**
   * Actualiza el src de cada cabinet según su estado MQTT actual.
   * Llamada al montar y en cada cambio del store de MQTT/mock.
   */
  function updateCabinetStates() {
    if (!useSentinel) return;
    for (const c of cabinets) {
      const newSrc = cabinetSrcFor(c.id);
      if ((c.ray as any).src !== newSrc) {
        (c.ray as any).src = newSrc;
      }
    }
  }
  updateCabinetStates();
  let stateThrottle: number | null = null;
  const scheduleStateUpdate = () => {
    if (stateThrottle != null) return;
    stateThrottle = window.setTimeout(() => {
      stateThrottle = null;
      updateCabinetStates();
    }, 250);
  };
  const unsubMqttState = useColomaMqtt.subscribe(scheduleStateUpdate);
  const unsubMockState = useMockMode.subscribe(updateCabinetStates);
  const unsubMasksState = useAlarmMasks.subscribe(updateCabinetStates);
  const unsubMutesState = useSessionAlarmMutes.subscribe(updateCabinetStates);
  rebuildPins();
  const unsubPins = usePinControls.subscribe(() => {
    rebuildPins();
    applyModelColor();
    applyModelScale();
  });

  // Color del modelo 3D (rayo + halo). En modo "Simular red" se fuerza
  // verde como override; en estado normal toma el color del store
  // (configurable desde el panel de sliders). Asignar `strokeColor` /
  // `fillColor` no toca la geometría — barato y sin flickering.
  const applyModelColor = () => {
    const isMock = useMockMode.getState().enabled;
    const color = isMock ? COLOR_MOCK_ONLINE : usePinControls.getState().modelColor;
    const fillRgba = `rgba(${hexToRgb(color)}, 0.30)`;
    for (const c of cabinets) {
      (c.ray as any).strokeColor = color;
      if (c.halo) {
        (c.halo as any).strokeColor = color;
        (c.halo as any).fillColor = fillRgba;
      }
    }
  };
  applyModelColor();
  const unsubMock = useMockMode.subscribe(applyModelColor);

  /**
   * Escalar todos los markers al cruzar un umbral de zoom (range). El
   * cambio se hace solo cuando `tierFor(range)` devuelve un Tier distinto
   * del actual — así evitamos flickering por gesto continuo. El
   * `strokeWidth` se actualiza siempre porque no causa rebuild.
   */
  function applyTier(t: Tier) {
    currentTier = t;
    for (const c of cabinets) {
      // Con el modelo Sentinel la geometría es fija (no se escala con el
      // zoom). Solo el halo y el pin se reajustan.
      if (!useSentinel) {
        (c.ray as any).strokeWidth = t.strokeWidth;
        (c.ray as any).coordinates = [
          { lat: c.lat, lng: c.lng, altitude: 0 },
          { lat: c.lat, lng: c.lng, altitude: t.rayHeight },
        ];
      }
      if (c.halo) {
        (c.halo as any).outerCoordinates = ringCoords(c.lat, c.lng, t.haloRadiusDeg);
      }
      if (c.label) {
        const s = effectiveScale();
        const labelAlt = useSentinel
          ? CABINET_HEIGHT_M * s + 1
          : t.rayHeight + t.labelOffset;
        (c.label as any).position = {
          lat: c.lat,
          lng: c.lng,
          altitude: labelAlt,
        };
      }
    }
  }
  // Cache del estado "vista calle" para reconstruir pins solo al cruzar
  // el umbral CLOSE_ZOOM (evitamos rebuild en cada tick de rueda).
  let prevClose = false;
  const onRangeChange = () => {
    const range = (mapEl as any).range ?? 0;
    const next = tierFor(range);
    if (next !== currentTier) applyTier(next);
    // Cuando autoScale está ON, recalcular la escala del cabinet en
    // cada cambio de range. Más barato que rebuild — solo asigna
    // `scale` en las 79 instancias.
    if (usePinControls.getState().autoScale) applyModelScale();
    // Reconstruir pins cuando el operador cruza al modo "vista calle"
    // — el color y tamaño cambian al cyan grande para legibilidad sin
    // ruido en vista lejana.
    const close = isCloseView();
    if (close !== prevClose) {
      prevClose = close;
      rebuildPins();
    }
  };
  mapEl.addEventListener('gmp-rangechange', onRangeChange);
  // Snapshot inicial — por si la cámara llega con range distinto del default.
  setTimeout(onRangeChange, 200);

  // Toggle de la capa de reguladores. Cuando `regulatorsEnabled` cae a
  // false, ocultamos sin destruir (mantenemos la geometría en memoria
  // para reactivar sin volver a descargar el .glb del Sentinel).
  const applyVisibility = () => {
    const visible = usePinControls.getState().regulatorsEnabled;
    const cssVis = visible ? '' : 'hidden';
    for (const c of cabinets) {
      c.ray.style.visibility = cssVis;
      if (c.halo) c.halo.style.visibility = cssVis;
      if (c.label) c.label.style.visibility = cssVis;
    }
  };
  applyVisibility();
  const unsubVisibility = usePinControls.subscribe((s, prev) => {
    if (s.regulatorsEnabled !== prev.regulatorsEnabled) applyVisibility();
  });

  return () => {
    mapEl.removeEventListener('gmp-rangechange', onRangeChange);
    if (stateThrottle != null) clearTimeout(stateThrottle);
    unsubMock();
    unsubPins();
    unsubVisibility();
    unsubMqttState();
    unsubMockState();
    unsubMasksState();
    unsubMutesState();
    listeners.forEach(l => l.el.removeEventListener(l.type, l.fn));
    for (const c of cabinets) {
      c.ray.remove();
      c.halo?.remove();
      c.label?.remove();
    }
  };
}


// ════════════════════════════════════════════════════════════════════
//  mountRealisticGroupTrafficLights — 1 cabezal por GRUPO
// ════════════════════════════════════════════════════════════════════

/**
 * Coloca un cabezal de semáforo DGT (Model3DInteractiveElement con
 * tl-{red,amber,green}.glb) en la posición y bearing de cada
 * grupo del cruce. La variante se elige según el `ColorCode` actual
 * del grupo, derivado de:
 *   - MQTT real (`useColomaMqtt`) si el regulador está online.
 *   - Simulación local (`useDetailedSimulation`) si no hay MQTT
 *     o `Simular red` está activo.
 *   - Apagado: el modelo queda con `visibility:hidden` (no se ve).
 *
 * El módulo es imperativo y se suscribe directo a los stores Zustand
 * — equivalente a mountRealisticCabinetMarkers pero ortogonal: no
 * comparte estado, montaje ni cleanup. Esto facilita activar/desactivar
 * la capa de grupos sin afectar a los markers de cabinet.
 *
 * Mapeo p-code UNE → ColorCode: replicado de useIntersectionState.ts:24-29
 * (un único pcodeToColor por todo el frontend; en una refactor futura
 * podríamos extraerlo a un módulo compartido).
 */

const PCODE_TO_COLOR: Record<number, CC> = {
  0: CC.D, 1: CC.V, 2: CC.A, 3: CC.R, 4: CC.N,
  5: CC.S, 6: CC.J, 7: CC.I, 8: CC.F, 9: CC.C,
  10: CC.P, 11: CC.G, 14: CC.B, 15: CC.H, 16: CC.K,
  22: CC.E, 39: CC.Z,
};

const TL_SRC_BY_COLOR: Partial<Record<CC, string>> = {
  [CC.V]: '/models/tl-green.glb',
  [CC.A]: '/models/tl-amber.glb',
  [CC.R]: '/models/tl-red.glb',
  // Estados parpadeantes caen al color principal correspondiente.
  [CC.S]: '/models/tl-green.glb',  // verde parpadeante
  [CC.C]: '/models/tl-amber.glb',  // ámbar parpadeante
  [CC.F]: '/models/tl-red.glb',    // rojo parpadeante
  [CC.G]: '/models/tl-green.glb',  // verde rápido parpadeante
  [CC.E]: '/models/tl-amber.glb',  // ámbar rápido parpadeante
  [CC.P]: '/models/tl-green.glb',  // verde peatonal
  [CC.H]: '/models/tl-red.glb',    // rojo peatonal
  [CC.I]: '/models/tl-green.glb',  // verde peatonal parpadeante
  [CC.B]: '/models/tl-amber.glb',  // rojo+ámbar → amber (preparación)
  // Cabezal apagado: las 3 lentes mate, sin emisivo. Reemplaza el
  // `visibility:hidden` anterior — el operador ve una "silueta" oscura
  // donde el grupo está dark / nocturno en vez de un hueco visual.
  [CC.D]: '/models/tl-off.glb',    // apagado (dark)
  [CC.N]: '/models/tl-off.glb',    // negro (todo apagado, distinto de D)
  // J, K, Z (estados error/alternantes) → ocultar modelo (fallback).
};

const PHASES: Array<'F1' | 'F2' | 'F3' | 'F4'> = ['F1', 'F2', 'F3', 'F4'];

/** Tamaño de los cabezales sobre el mapa (factor sobre 1 m real). */
const TL_SCALE = 6;
// Altura sobre el terreno (m) — antes constante TL_ALTITUDE_M=20. Ahora
// vive en `usePinControls.lightAltitude` (default 20) para que el slider
// "Altura sem." del panel ajuste en vivo. El cabezal real cuelga ~3-4 m
// pero a esa altura los edificios fotogramétricos de Santa Coloma lo
// ocultan; el operador sube la altura hasta tener lectura limpia.
/**
 * Range máximo de cámara (m) al que mostramos los traffic-lights por
 * grupo. Por encima se ocultan — equivalente al `GROUP_ZOOM_THRESHOLD`
 * de MapLibre. 316 modelos visibles desde 5 km de altura paralizan el
 * navegador; cargarlos sólo al acercarse evita el bloqueo y libera GPU
 * cuando el operador está en vista municipio.
 */
const TL_VISIBLE_RANGE_M = 800;
/** Umbral de zoom MapLibre por debajo del cual los IDs de semáforo se
 *  ocultan aunque el toggle "IDs sem." esté ON. A zoom < 16.5 los 290
 *  labels se solapan y son ruido — se mantienen los semáforos pero los
 *  IDs solo aparecen cuando la cámara está suficientemente cerca para
 *  leerlos. Modo mantenimiento ARS93. */
const ID_LABEL_MIN_ZOOM = 16.5;

export function mountRealisticGroupTrafficLights(
  mapEl: HTMLElement,
  opts: MountOptions,
): () => void {
  const maps3d = (window as any).google?.maps?.maps3d;
  if (!maps3d?.Model3DInteractiveElement) {
    return () => undefined;
  }
  const Model3DInteractiveElement = maps3d.Model3DInteractiveElement;
  const Marker3DElement = maps3d.Marker3DElement;
  const PinElement = (window as any).google?.maps?.marker?.PinElement;
  const AltitudeMode = maps3d.AltitudeMode;
  const altMode = AltitudeMode?.RELATIVE_TO_GROUND ?? 'RELATIVE_TO_GROUND';

  interface Light {
    model: HTMLElement | null;        // null mientras no esté montado
    /** etiqueta discreta con ID compuesto encima del cabezal — modo
     *  mantenimiento para que ARS93 construya la tabla real. Null si
     *  `lightIdsVisible` está OFF o aún no montada. */
    idLabel: HTMLElement | null;
    /** ID compuesto pre-calculado: `029.g3·osm` (cabinet.grupo). */
    displayId: string;
    intersection: IntersectionDefinition;
    groupIdx: number;
    cabinetId: number;
    lat: number;
    lng: number;
    bearing: number;
    /** ColorCode actual aplicado (para evitar reasignar src si no cambia). */
    appliedColor: CC | null;
  }

  function buildDisplayId(cabinetId: number, groupId: number): string {
    return `${toDisplayId(cabinetId)}.g${groupId}·osm`;
  }
  const osmLights: Light[] = [];
  const listeners: Array<{ el: HTMLElement; type: string; fn: (e: Event) => void }> = [];

  // osmLights — uno por nodo OSM físico mapeado a un grupo (cableado real,
  //    con overrides de Modo Edición ya aplicados vía osmGroupMappingResolved).
  //    El bearing se resuelve aquí (estático por light):
  //       tag.traffic_signals:direction === 'forward'  → group.bearing
  //       tag.traffic_signals:direction === 'backward' → (group.bearing + 180) % 360
  //       sin tag                                       → group.bearing
  const intersectionByCabinetId = new Map<number, IntersectionDefinition>();
  for (const ix of opts.intersections) {
    intersectionByCabinetId.set(ix.cabinet.logicalId, ix);
  }
  const osmSignalById = new Map<number, { position: [number, number]; tags: Record<string, string> }>();
  for (const s of santaColomaStaticSignals) {
    osmSignalById.set(s.id, { position: s.position, tags: s.tags });
  }
  for (const m of osmGroupMapping) {
    const ix = intersectionByCabinetId.get(m.cabinetId);
    const signal = osmSignalById.get(m.osmId);
    if (!ix || !signal) continue;
    const groupIdx = ix.groups.findIndex(g => g.id === m.groupId);
    if (groupIdx < 0) continue;
    const group = ix.groups[groupIdx];
    const [lng, lat] = signal.position;
    let bearing = group.bearing;
    if (m.osmDirection === 'backward') bearing = (group.bearing + 180) % 360;
    osmLights.push({
      model: null,
      idLabel: null,
      displayId: buildDisplayId(m.cabinetId, m.groupId),
      intersection: ix,
      groupIdx,
      cabinetId: m.cabinetId,
      lat,
      lng,
      bearing,
      appliedColor: null,
    });
  }

  // Solo semáforos con cableado real confirmado — sin fallback sintético.
  // La posición sintética (offset cardinal ciego desde el cabinet) no tiene
  // relación con la calle real y llegaba a caer en medio de manzanas/zonas
  // sin ninguna vía (reportado por ARS93 2026-09-18, mismo fix aplicado ya
  // en la vista operativa — IntersectionMarkers.tsx).
  function activeLightSet(): Light[] {
    return osmLights;
  }

  // Lights actualmente con modelo montado (todos los que `reconcileMount`
  // ha materializado). Mantenido en paralelo a `l.model != null` para
  // iterar barato en updateColors / applyAltitude sin recorrer ambos sets.
  const mountedLights = new Set<Light>();

  /** Altura del label de ID sobre el terreno (m), relativa a la altura
   *  del cabezal. El .glb del cabezal mide ~1 m de alto, escalado por
   *  TL_SCALE=6 ocupa unos 6 m verticales desde `lightAltitude`. Sumamos
   *  ese ancho aproximado + un pequeño margen para que el label flote
   *  visiblemente por encima de la lente sin tocarla. */
  function labelAltitudeFor(modelAltitude: number): number {
    return modelAltitude + TL_SCALE + 2;
  }

  function mountIdLabel(l: Light) {
    if (l.idLabel || !Marker3DElement) return;
    const altitude = labelAltitudeFor(usePinControls.getState().lightAltitude);
    // Verificación empírica (esta sesión):
    //  - `<div>` o `<span>` como child quedan en el DOM con isConnected:true
    //    pero rect 0×0 — el SDK NO los rasteriza al WebGL canvas.
    //  - `Marker3DElement.label = string` es la única API que renderiza
    //    texto plano custom en 3D, pero el SDK adjunta un pin lágrima rojo
    //    default por debajo del texto.
    //  - Para suprimir ese pin se pasa un PinElement transparente como
    //    child — el SDK respeta el child y omite el pin default, dejando
    //    solo el texto flotante (confirmado en SCG000 antes del cambio).
    const label = new Marker3DElement({
      position: { lat: l.lat, lng: l.lng, altitude },
      altitudeMode: altMode,
      drawsWhenOccluded: true,
      label: l.displayId,
    }) as HTMLElement;
    if (PinElement) {
      const ghost = new PinElement({
        background: 'rgba(0,0,0,0)',
        borderColor: 'rgba(0,0,0,0)',
        glyphColor: 'rgba(0,0,0,0)',
        glyph: '',
        scale: 0.01,
      });
      label.replaceChildren(ghost.element);
    }
    mapEl.appendChild(label as Node);
    l.idLabel = label;
  }

  function unmountIdLabel(l: Light) {
    if (!l.idLabel) return;
    l.idLabel.remove();
    l.idLabel = null;
  }

  function mountLight(l: Light) {
    if (l.model) return;
    const altitude = usePinControls.getState().lightAltitude;
    const model = new Model3DInteractiveElement({
      position: { lat: l.lat, lng: l.lng, altitude },
      altitudeMode: altMode,
      src: '/models/tl-red.glb', // updateColors corrige al primer tick
      orientation: { heading: l.bearing, tilt: 0, roll: 0 },
      scale: { x: TL_SCALE, y: TL_SCALE, z: TL_SCALE },
    }) as HTMLElement;
    const onClick = () => useSelection.getState().toggle(l.cabinetId);
    model.addEventListener('gmp-click', onClick);
    listeners.push({ el: model, type: 'gmp-click', fn: onClick });
    mapEl.appendChild(model as Node);
    l.model = model;
    l.appliedColor = null;
    mountedLights.add(l);
    if (idsShouldBeVisible()) mountIdLabel(l);
  }

  /** Computa si los IDs deben verse en este momento. Es función pura del
   *  toggle del store + zoom actual de la cámara — sin estado intermedio. */
  function idsShouldBeVisible(): boolean {
    if (!usePinControls.getState().lightIdsVisible) return false;
    const range = (mapEl as any).range ?? 0;
    if (range <= 0) return false;
    const center = (mapEl as any).center;
    const lat = typeof center?.lat === 'function' ? center.lat() : (center?.lat ?? 41.45);
    return rangeToZoom(range, lat) >= ID_LABEL_MIN_ZOOM;
  }

  function unmountLight(l: Light) {
    if (!l.model) return;
    l.model.remove();
    l.model = null;
    l.appliedColor = null;
    mountedLights.delete(l);
    unmountIdLabel(l);
  }

  function unmountAll() {
    for (const l of [...mountedLights]) unmountLight(l);
  }

  // `reconcileMount` diferencia el set deseado contra `mountedLights`:
  // monta los que faltan, desmonta los que sobran. O(N) sobre N≈316,
  // barato — se llama solo al toggle de mockMode o al cruzar el umbral
  // de zoom (no por frame).
  function reconcileMount() {
    const desired = new Set(activeLightSet());
    for (const l of [...mountedLights]) {
      if (!desired.has(l)) unmountLight(l);
    }
    for (const l of desired) {
      if (!l.model) mountLight(l);
    }
  }

  // 2) Función pura para calcular ColorCode actual de un grupo.
  function computeGroupColor(light: Light): CC {
    const { intersection: ix, groupIdx } = light;
    const canonical = toCanonicalId(ix.cabinet.logicalId);
    const mqttStatus = useColomaMqtt.getState().reguladores[canonical];
    // MQTT real tiene prioridad si el regulador está online.
    const groupId = ix.groups[groupIdx].id;
    const mqttGroup = mqttStatus?.grupos?.find(gr => gr.grupo === groupId);
    if (mqttStatus?.online && mqttGroup) {
      return PCODE_TO_COLOR[mqttGroup.estado_une] ?? CC.D;
    }
    // Fallback simulación: mismo cálculo que useIntersectionState modo sim.
    // El plan/ciclo es global, los colores salen de group.phaseColors[fase].
    const mock = useMockMode.getState().enabled;
    if (!mock && !mqttStatus) return CC.D; // sin datos y sin demo → apagado
    const sim = useDetailedSimulation.getState();
    const offset = ix.phaseOffset ?? 0;
    let phase: string = sim.currentPhase;
    if (offset !== 0) {
      let offsetDs = 0;
      for (let i = 0; i < offset; i++) offsetDs += sim.phaseDurations[i];
      const shifted = (sim.cyclePosition + offsetDs) % sim.cycleTime;
      let acc = 0;
      for (let i = 0; i < 4; i++) {
        acc += sim.phaseDurations[i];
        if (shifted < acc) { phase = PHASES[i]; break; }
      }
    }
    const g = ix.groups[groupIdx];
    return g.phaseColors[phase] ?? CC.D;
  }

  // 3) Loop de actualización: por cada light montado, calcula color y
  //    reasigna src si cambió. Si el color no tiene .glb (J, K, Z),
  //    oculta — D y N ahora usan tl-off.glb (visible "off"), no se ocultan.
  function updateColors() {
    for (const l of mountedLights) {
      const color = computeGroupColor(l);
      if (color === l.appliedColor) continue;
      l.appliedColor = color;
      const src = TL_SRC_BY_COLOR[color];
      if (src) {
        if ((l.model as any).src !== src) (l.model as any).src = src;
        l.model!.style.visibility = 'visible';
      } else {
        l.model!.style.visibility = 'hidden';
      }
    }
  }

  // 4) Suscripciones — cualquier cambio en MQTT/sim/mockMode dispara
  //    updateColors. La sim cambia cyclePosition cada frame: throttling
  //    a 200 ms para que no recalcule 60 veces/seg.
  let throttle: number | null = null;
  const scheduleUpdate = () => {
    if (throttle != null) return;
    throttle = window.setTimeout(() => {
      throttle = null;
      updateColors();
    }, 200);
  };
  const unsubMqtt = useColomaMqtt.subscribe(scheduleUpdate);
  const unsubSim = useDetailedSimulation.subscribe(scheduleUpdate);
  const unsubMock = useMockMode.subscribe(updateColors);

  // 5) Mount/unmount. Composición FIJA: solo osmLights (cableado real
  //  confirmado). Sin datos MQTT y sin mock, `computeGroupColor` devuelve
  //  `CC.D` → `tl-off.glb` (cabezal apagado). El operador ve siempre la
  //  misma topología; el dato es lo que cambia.
  //
  //  Gates restantes:
  //     a) toggle "Semáforos" del panel (`lightsEnabled`) — global ON/OFF.
  //     b) range ≤ TL_VISIBLE_RANGE_M (800 m) — por encima oculta los
  //        modelos (GPU sufre, ruido visual desde vista municipio).
  //
  //  `reconcileMount` diferencia el set deseado contra `mountedLights` —
  //  evita rebuild al alternar (solo monta/desmonta lo que cambia).
  function syncMounting() {
    const range = (mapEl as any).range ?? 0;
    const enabled = usePinControls.getState().lightsEnabled;
    const inRange = enabled && range <= TL_VISIBLE_RANGE_M;
    if (!inRange) {
      if (mountedLights.size > 0) unmountAll();
      return;
    }
    reconcileMount();
    updateColors();
    // El gate de zoom para los IDs (≥ ID_LABEL_MIN_ZOOM) se reevalúa aquí
    // — al alejar la cámara los labels se desmontan; al acercar vuelven.
    applyIdsVisibility();
  }
  mapEl.addEventListener('gmp-rangechange', syncMounting);
  const unsubToggle = usePinControls.subscribe((s, prev) => {
    if (s.lightsEnabled !== prev.lightsEnabled) syncMounting();
  });
  setTimeout(syncMounting, 200);

  // 6) Slider "Altura sem." del panel — recalibra la altura de los 316
  //    cabezales en vivo. Mutamos `position` in-place (mismo lat/lng,
  //    nueva altitude); el SDK acepta la reasignación sin remount del
  //    modelo. Throttle a 150 ms para que arrastrar el slider no spam-
  //    actualice todos los modelos a 60 fps (basta con ~7 fps para
  //    percepción fluida sin saturar al SDK).
  let altThrottle: number | null = null;
  let pendingAltitude: number | null = null;
  function applyAltitude(altitude: number) {
    const labelAlt = labelAltitudeFor(altitude);
    for (const l of mountedLights) {
      (l.model as any).position = { lat: l.lat, lng: l.lng, altitude };
      if (l.idLabel) {
        (l.idLabel as any).position = { lat: l.lat, lng: l.lng, altitude: labelAlt };
      }
    }
  }

  /** Toggle "IDs sem." + gate por zoom — monta/desmonta los labels en
   *  masa sin tocar los modelos 3D ni los listeners. Se llama tanto al
   *  cambiar el toggle como al cruzar el umbral de zoom (gmp-rangechange).
   *  Sólo afecta a lights ya montados. */
  function applyIdsVisibility() {
    const visible = idsShouldBeVisible();
    if (visible) {
      for (const l of mountedLights) if (!l.idLabel) mountIdLabel(l);
    } else {
      for (const l of mountedLights) unmountIdLabel(l);
    }
  }
  const scheduleAltitude = (altitude: number) => {
    pendingAltitude = altitude;
    if (altThrottle != null) return;
    altThrottle = window.setTimeout(() => {
      altThrottle = null;
      if (pendingAltitude != null) applyAltitude(pendingAltitude);
      pendingAltitude = null;
    }, 150);
  };
  const unsubAltitude = usePinControls.subscribe((s, prev) => {
    if (s.lightAltitude !== prev.lightAltitude) scheduleAltitude(s.lightAltitude);
  });
  const unsubIds = usePinControls.subscribe((s, prev) => {
    if (s.lightIdsVisible !== prev.lightIdsVisible) applyIdsVisibility();
  });

  return () => {
    mapEl.removeEventListener('gmp-rangechange', syncMounting);
    unsubToggle();
    unsubAltitude();
    unsubIds();
    if (throttle != null) clearTimeout(throttle);
    if (altThrottle != null) clearTimeout(altThrottle);
    unsubMqtt();
    unsubSim();
    unsubMock();
    listeners.forEach(l => l.el.removeEventListener(l.type, l.fn));
    unmountAll();
  };
}
